/* eslint-disable @typescript-eslint/no-explicit-any */
import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * Phase 6 Vendor-Port W6 — FAN selector data layer (§33.1).
 *
 * The fan selector's WRITES (all CODE-ONLY, userArmed-gated in the component):
 *   1. RATE        → POST /api/v1.0/rating/ {part:'fan'} (×up-to-3 vendor sites)
 *      + the per-fan curve POST — the SHARED useRatingDispatch (ratingApi). NOT
 *      re-declared here.
 *   2. ISP/ESP     → POST /api/v1.0/ahu/fans/<id>/isp {…fan data}  (postFanIsp)
 *   3. curve persist → PUT /api/v1.0/ahu/fans/curves/<id> {bhp,cfm:'',sp,
 *      system_curve} (saveFanCurves) — fires on Apply BEFORE the row's curve
 *      keys are stripped (§33.1).
 *   4. SELECT (apply) → current_model.set via the form shim (the component
 *      auto-save) — routed through __FORM_SHIM__, not an endpoint here.
 *   5. ISOLATOR resolve → PATCH /api/v1.0/ahu/fans/<id> {master_fan_id,
 *      master_motor_id, si_specified_deflection_id, fmbase, si_params}
 *      (patchFanIsolators) — fires when the Isolation OUTPUT pane is opened on
 *      a rated-but-not-yet-Selected fan, exactly as dev's chooseIsolation does
 *      (js:7540). This is a write on a READ action; it is dev's behaviour, not
 *      ours (see the endpoint comment).
 *
 * READS (fire on open — read-only, smoke-safe):
 *   - getFanCurves: GET /api/v1.0/ahu/fans/curves/<id> — the STORED curve for a
 *     rated fan (538 fans on dev3 have one); rendered on open by the canvas, NO
 *     fetch beyond this single read-only GET (legacy getCurves model_fetcher).
 *   - getFanNotes: GET cms/1/.../fans/notes/.
 *
 * ★ DATA-SAFETY: the only GETs fire on open; postFanIsp / saveFanCurves are
 * declared here but called ONLY from the userArmed Rate/Select paths. The
 * read-only SMOKE fires getFanCurves + getFanNotes only → 0 mutating + 0
 * /rating/.
 */

export interface FanNote {
  note: string;
  [key: string]: unknown;
}

export interface FanCurves {
  id?: number;
  /** JSON-stringified [[x,y],...] arrays (legacy CurvesModel). */
  bhp?: string | null;
  cfm?: string | null;
  sp?: string | null;
  system_curve?: string | null;
  [key: string]: unknown;
}

export interface FanIspResponse {
  isp?: number | null;
  esp?: number | null;
  apd?: number | null;
  [key: string]: unknown;
}

export const fanSelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // STORED curve for a rated fan (legacy CurvesModel GET — getCurves
    // model_fetcher). Read-only; rendered on open. Missing curve → 404/empty.
    getFanCurves: builder.query<FanCurves | null, number>({
      query: (fanId) => `/api/v1.0/ahu/fans/curves/${fanId}`,
      transformResponse: (res: unknown): FanCurves | null => {
        if (!res || typeof res !== 'object') return null;
        const o = res as FanCurves;
        // a bare {id} (no curve keys) means "no stored curve".
        if (Object.keys(o).length <= 1) return null;
        return o;
      },
    }),

    // fan rating notes (legacy NotesCollection — fan/model/notes.js).
    getFanNotes: builder.query<FanNote[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): FanNote[] => {
        if (Array.isArray(res)) return res as FanNote[];
        const o = res as { notes?: FanNote[]; results?: FanNote[] } | null;
        return o?.notes ?? o?.results ?? [];
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // air streams (legacy air CMS air_stream node) — read for the Performance
    // air-stream select.
    getFanAirStreams: builder.query<any[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): any[] => {
        if (Array.isArray(res)) return res;
        const o = res as { air_stream?: any[]; results?: any[] } | null;
        return o?.air_stream ?? o?.results ?? [];
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // per-fan-type mfg settings (legacy FanSettings — model/fan.cms.setting.js,
    // fetched by updateQty at js:3805). GET
    //   cms/1/v1.0.0/ahu/fans/<type>_arr<arr>/mfg/settings/
    // returns ONE object; only max_qty_wide / max_qty_high are read here, to
    // build the Qty Wide x High option ranges. Read-only.
    getFanMfgSettings: builder.query<Record<string, unknown> | null, string>({
      query: (url) => url,
      transformResponse: (res: unknown): Record<string, unknown> | null =>
        res && typeof res === 'object' && !Array.isArray(res)
          ? (res as Record<string, unknown>)
          : null,
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // "Available Motors: N" — legacy getMotorCount (fan.selector.js:2087-2172)
    // filters the motor catalogue by the current motor + fan params and shows
    // the count above the Motor tab. GET .../ahu/motors/all/count/?<filters>,
    // response {count: N}. Read-only.
    getFanMotorCount: builder.query<number | null, string>({
      query: (url) => url,
      transformResponse: (res: unknown): number | null => {
        const n = (res as { count?: unknown } | null)?.count;
        return n == null ? null : Number(n);
      },
    }),

    // a generic master/CMS list by URL (spacing selects, mountings, isolator/
    // motor-base docs go through getComponentCmsDoc; this is for flat lists).
    getFanListByUrl: builder.query<any[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): any[] =>
        Array.isArray(res) ? res : ((res as any)?.results ?? []),
      keepUnusedDataFor: 24 * 60 * 60,
    }),

    // ----- WRITES (CODE-ONLY, userArmed) -----

    // ISP/ESP round-trip (legacy get_isp_esp → POST ahu/fans/<id>/isp).
    postFanIsp: builder.mutation<
      FanIspResponse,
      { fanId: number; data: Record<string, unknown> }
    >({
      query: ({ fanId, data }) => ({
        url: `/api/v1.0/ahu/fans/${fanId}/isp/`,
        method: 'POST',
        body: data,
      }),
    }),

    // curve persist (legacy CurvesModel save — PUT ahu/fans/curves/<id>). Fired
    // on Apply only (userArmed), before the chosen row's curve keys are stripped.
    saveFanCurves: builder.mutation<
      FanCurves,
      { fanId: number; data: FanCurves }
    >({
      query: ({ fanId, data }) => ({
        url: `/api/v1.0/ahu/fans/curves/${fanId}`,
        method: 'PUT',
        body: { id: fanId, ...data },
      }),
    }),

    // ISOLATOR resolve (legacy chooseIsolation's middle branch —
    // `self.fanModel.save(self.isolatorSaveData, {patch: true})`,
    // fan.selector.js:7540). `AHUFan.isolators` is a computed property with no
    // read-only endpoint of its own: the four locations only resolve once the
    // chosen fan/motor + si_params are ON the record, so dev persists them and
    // renders the PATCH response. Deliberately NOT invalidating the fan detail
    // — legacy patches a SEPARATE FanModel and leaves `current_model` alone, so
    // the dialog's other panes keep showing the pre-Select values.
    patchFanIsolators: builder.mutation<
      Record<string, any>,
      { fanId: number; data: Record<string, unknown> }
    >({
      query: ({ fanId, data }) => ({
        url: `/api/v1.0/ahu/fans/${fanId}`,
        method: 'PATCH',
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFanCurvesQuery,
  useGetFanNotesQuery,
  useGetFanAirStreamsQuery,
  useGetFanMfgSettingsQuery,
  useGetFanMotorCountQuery,
  useGetFanListByUrlQuery,
  usePostFanIspMutation,
  useSaveFanCurvesMutation,
  usePatchFanIsolatorsMutation,
} = fanSelectorApi;
